import { useState, useEffect } from 'react'
import flowIcon from '@assets/image_1777447102475.png'
import { track } from '../analytics.js'
import { loadVisitCount } from '../storage.js'

const DISMISSED_KEY  = 'flow_a2hs_v3'
const MIN_VISIT_COUNT = 3

const COLLAPSED = 0
const STEP1     = 1
const STEP2     = 2
const DONE      = 3

export default function AddToHomeScreen({ bgMode = 'dark' }) {
  const [step, setStep]           = useState(COLLAPSED)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Already dismissed
    try { if (localStorage.getItem(DISMISSED_KEY)) { setDismissed(true); return } } catch {}

    // Already installed as standalone
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    if (isStandalone) { setDismissed(true); return }

    // Only show on mobile devices
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    if (!isMobile) { setDismissed(true); return }

    // Only show after 3+ visits
    if (loadVisitCount() < MIN_VISIT_COUNT) { setDismissed(true); return }
  }, [])

  const dismiss = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation() }
    setDismissed(true)
    try { localStorage.setItem(DISMISSED_KEY, '1') } catch {}
  }

  const expand = () => {
    setStep(STEP1)
    track('pwa_tutorial_shown', { platform: /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android' })
  }

  if (dismissed) return null

  const dark = bgMode === 'dark'

  const bannerStyle = {
    position: 'relative',
    marginTop: 16,
    background: dark
      ? 'linear-gradient(135deg, rgba(34,197,94,0.09), rgba(20,80,40,0.16))'
      : 'linear-gradient(135deg, rgba(34,197,94,0.11), rgba(20,120,60,0.07))',
    border: dark
      ? '1px solid rgba(34,197,94,0.2)'
      : '1px solid rgba(22,163,74,0.32)',
    borderRadius: 14,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    overflow: 'hidden',
    animation: 'fadeUp 0.35s ease',
  }

  const dismissBtn = (
    <button
      onClick={dismiss}
      aria-label="Dismiss"
      style={{
        position: 'absolute', top: 6, right: 6,
        background: 'transparent', border: 'none',
        color: dark ? 'rgba(140,200,150,0.5)' : 'rgba(20,80,40,0.4)',
        cursor: 'pointer', fontSize: 18, lineHeight: 1,
        width: 28, height: 28, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
      }}
    >×</button>
  )

  const nextBtn = (label, onClick) => (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '11px 0',
        background: 'linear-gradient(135deg, #16a34a, #15803d)',
        border: 'none', borderRadius: 10,
        color: '#fff', fontSize: 13, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
        WebkitTapHighlightColor: 'transparent',
        boxShadow: '0 3px 12px rgba(22,163,74,0.28)',
        marginTop: 14,
      }}
    >{label}</button>
  )

  /* ── Collapsed: compact row ── */
  if (step === COLLAPSED) {
    return (
      <div style={bannerStyle}>
        <button
          onClick={expand}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            width: '100%', background: 'none', border: 'none',
            padding: '14px 44px 14px 14px', cursor: 'pointer',
            textAlign: 'left', fontFamily: 'inherit',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{
            width: 36, height: 36, flexShrink: 0, borderRadius: 10,
            background: dark ? 'rgba(34,197,94,0.16)' : 'rgba(22,163,74,0.13)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>📲</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, marginBottom: 2,
              color: dark ? '#e8f5ea' : '#14532d',
            }}>Add Flow to your home screen</div>
            <div style={{
              fontSize: 11, lineHeight: 1.45,
              color: dark ? 'rgba(140,200,150,0.75)' : 'rgba(20,80,40,0.68)',
            }}>Launch it like an app — no App Store needed</div>
          </div>
          <div style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
            color: dark ? '#86efac' : '#15803d',
            background: dark ? 'rgba(34,197,94,0.12)' : 'rgba(22,163,74,0.12)',
            border: dark ? '1px solid rgba(34,197,94,0.25)' : '1px solid rgba(22,163,74,0.35)',
            padding: '5px 9px', borderRadius: 7, whiteSpace: 'nowrap', flexShrink: 0,
          }}>HOW?</div>
        </button>
        {dismissBtn}
      </div>
    )
  }

  /* ── Expanded tutorial steps ── */
  const stepLabel = step === STEP1 ? 'Step 1 of 2' : step === STEP2 ? 'Step 2 of 2' : '🎉 All set!'

  return (
    <div style={{ ...bannerStyle, padding: '16px 16px 14px' }}>
      {dismissBtn}

      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        color: dark ? 'rgba(134,239,172,0.65)' : '#15803d',
        marginBottom: 12, paddingRight: 28,
      }}>{stepLabel}</div>

      {step === STEP1 && <>
        <div style={{ fontSize: 28, marginBottom: 8 }}>📤</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: dark ? '#c8e6cb' : '#1e2922', marginBottom: 6 }}>
          Open your browser menu
        </div>
        <div style={{ fontSize: 12, color: dark ? 'rgba(200,228,242,0.55)' : 'rgba(30,80,50,0.6)', lineHeight: 1.65 }}>
          <strong style={{ color: dark ? '#c8e6cb' : '#1e2922' }}>iPhone:</strong> Tap the{' '}
          <strong style={{ color: dark ? '#c8e6cb' : '#1e2922' }}>Share</strong> icon (box with arrow ↑) at the bottom of Safari.<br />
          <strong style={{ color: dark ? '#c8e6cb' : '#1e2922' }}>Android:</strong> Tap the{' '}
          <strong style={{ color: dark ? '#c8e6cb' : '#1e2922' }}>⋮ three-dot menu</strong> at the top right of Chrome.
        </div>
        {nextBtn('Got it — Next →', () => setStep(STEP2))}
      </>}

      {step === STEP2 && <>
        <div style={{ fontSize: 28, marginBottom: 8 }}>➕</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: dark ? '#c8e6cb' : '#1e2922', marginBottom: 6 }}>
          Tap "Add to Home Screen"
        </div>
        <div style={{ fontSize: 12, color: dark ? 'rgba(200,228,242,0.55)' : 'rgba(30,80,50,0.6)', lineHeight: 1.65 }}>
          Scroll through the options and tap{' '}
          <strong style={{ color: dark ? '#c8e6cb' : '#1e2922' }}>"Add to Home Screen"</strong>.
          Confirm the name and tap <strong style={{ color: dark ? '#c8e6cb' : '#1e2922' }}>Add</strong>.<br /><br />
          Flow will appear on your home screen just like a real app!
        </div>
        {nextBtn('Done ✓', () => setStep(DONE))}
      </>}

      {step === DONE && (
        <div style={{ textAlign: 'center', padding: '4px 0 2px' }}>
          <img src={flowIcon} alt="Flow" style={{ width: 48, height: 48, borderRadius: 12, marginBottom: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.28)' }} />
          <div style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontStyle: 'italic', fontSize: 20,
            color: dark ? '#c8e6cb' : '#1e2922', marginBottom: 5,
          }}>You're all set!</div>
          <div style={{ fontSize: 12, color: dark ? 'rgba(200,228,242,0.5)' : 'rgba(30,80,50,0.55)', lineHeight: 1.55, marginBottom: 4 }}>
            Flow is now on your home screen.<br />Launch it anytime like a real app.
          </div>
          {nextBtn('Awesome! 🎉', dismiss)}
        </div>
      )}
    </div>
  )
}
